import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../i18n';
import {
  ApiError,
  type EmailSettingsResponse,
  type EmailTestResponse,
  getEmailSettings,
  sendTestEmail,
  updateEmailSettings,
} from '../../../lib/api';
import { queryKeys } from '../../../lib/queryKeys';
import { showToast } from '../../../lib/toast';
import {
  Callout,
  type CalloutTone,
  Card,
  CardHeader,
  Chip,
  EmptyRow,
  MasterDetailSkeleton,
  Tile,
} from '../../../ui/Primitives';
import {
  checkboxRowStyle,
  checkboxStyle,
  fieldLabelStyle,
  hintStyle,
  primaryButtonStyle,
  quietButtonStyle,
  textFieldStyle,
} from '../../../ui/styles';

import {
  type EmailDraft,
  emailDraftDirty,
  emailDraftFrom,
  emailRequestFrom,
  emailSavedSignature,
} from './emailDraft';

/**
 * SETTINGS → EMAIL & NOTIFICATIONS (SIR-7804), ported from BundleUp's
 * `web/src/routes/admin/AdminEmail.tsx` into Accounts' own primitives: the status strip,
 * the org-wide master switch, the sender identity, the Event | Email | In-app table, Save,
 * and a test send. Admin only: the route guard refuses anyone else, and the server 403s
 * every one of these reads and writes independently.
 *
 * The SendGrid key is write-only. The server returns only whether one is configured and its
 * last four characters; the field starts empty on every mount and is never filled from a
 * response.
 */
export function EmailSettingsSection() {
  const query = useQuery({ queryKey: queryKeys.emailSettings, queryFn: getEmailSettings });

  const saved = query.data;
  if (saved === undefined) {
    if (query.isError) {
      return (
        <div style={{ padding: '32px 40px' }}>
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {query.error instanceof ApiError ? query.error.message : STR.errors.generic}
          </Callout>
        </div>
      );
    }
    return <MasterDetailSkeleton />;
  }

  return (
    <div style={{ padding: '32px 40px 110px', minWidth: 0 }}>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <StatusStrip saved={saved} />
        {/* Keyed on the saved values, so a refetch that changes them remounts the form with
            a clean draft. A landed save also resets the draft itself (below): its response
            can carry the same values, e.g. a new key with the same last four. */}
        <EmailSettingsForm key={emailSavedSignature(saved)} saved={saved} />
      </div>
    </div>
  );
}

function StatusStrip({ saved }: { saved: EmailSettingsResponse }) {
  const { status } = saved;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        <Tile label={STR.settings.email.statusKey}>
          <Chip tone={status.sendGridApiKeyConfigured ? 'positive' : 'attention'}>
            {status.sendGridApiKeyConfigured
              ? STR.settings.email.keyConfigured(status.sendGridApiKeyLast4)
              : STR.settings.email.keyMissing}
          </Chip>
        </Tile>
        <Tile label={STR.settings.email.statusMaster}>
          <Chip tone={status.masterEnabled ? 'positive' : 'neutral'}>
            {status.masterEnabled ? STR.settings.email.on : STR.settings.email.off}
          </Chip>
        </Tile>
        <Tile label={STR.settings.email.statusFrom}>
          <span style={{ overflowWrap: 'anywhere' }}>{status.fromAddress}</span>
        </Tile>
      </div>
      {status.driver === 'capture' ? (
        <Callout tone="neutral">{STR.settings.email.captureNote}</Callout>
      ) : null}
    </div>
  );
}

function EmailSettingsForm({ saved }: { saved: EmailSettingsResponse }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<EmailDraft>(() => emailDraftFrom(saved));
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: updateEmailSettings,
    onSuccess: (response) => {
      // Always start a fresh draft from the response: the typed key leaves component state
      // even when the response matches what was already saved.
      setDraft(emailDraftFrom(response));
      queryClient.setQueryData(queryKeys.emailSettings, response);
      setError(null);
      showToast(STR.toast.emailSettingsSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const dirty = emailDraftDirty(draft, saved);
  const keySet = saved.status.sendGridApiKeyConfigured;

  function set<K extends keyof EmailDraft>(key: K, value: EmailDraft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
    setError(null);
  }

  function setEvent(eventKey: string, field: 'emailEnabled' | 'inAppEnabled', value: boolean) {
    setDraft((previous) => ({
      ...previous,
      events: previous.events.map((event) =>
        event.key === eventKey ? { ...event, [field]: value } : event,
      ),
    }));
    setError(null);
  }

  function save() {
    const built = emailRequestFrom(draft);
    if (!built.ok) {
      setError(STR.settings.email[built.problem]);
      return;
    }
    saveMutation.mutate(built.body);
  }

  return (
    <>
      <Card>
        <CardHeader title={STR.settings.email.title} />
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error === null ? null : (
            <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
              {error}
            </Callout>
          )}

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={draft.masterEnabled}
              onChange={(event) => {
                set('masterEnabled', event.target.checked);
              }}
              style={checkboxStyle}
            />
            <span>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
                {STR.settings.email.masterLabel}
              </span>
              <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
                {STR.settings.email.masterHint}
              </span>
            </span>
          </label>

          <div>
            <label style={{ display: 'block' }}>
              <span className="tx-muted" style={fieldLabelStyle}>
                {STR.settings.email.keyLabel}
              </span>
              <input
                className={error === null ? 'field' : 'field field-over'}
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                disabled={draft.clearKey}
                placeholder={
                  keySet
                    ? STR.settings.email.keyPlaceholderSet(saved.status.sendGridApiKeyLast4)
                    : STR.settings.email.keyPlaceholderMissing
                }
                value={draft.apiKey}
                onChange={(event) => {
                  set('apiKey', event.target.value);
                }}
                style={textFieldStyle}
              />
              <span className="tx-subtle" style={hintStyle}>
                {draft.clearKey ? STR.settings.email.clearKeyPending : STR.settings.email.keyHint}
              </span>
            </label>
            {keySet ? (
              <button
                type="button"
                className="tbtn"
                style={{ ...quietButtonStyle, marginTop: 8 }}
                onClick={() => {
                  setDraft((previous) => ({
                    ...previous,
                    apiKey: '',
                    clearKey: !previous.clearKey,
                  }));
                  setError(null);
                }}
              >
                {draft.clearKey ? STR.settings.email.keepKey : STR.settings.email.clearKey}
              </button>
            ) : null}
          </div>

          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.settings.email.fromLabel}
            </span>
            <input
              className={error === null ? 'field' : 'field field-over'}
              type="email"
              autoComplete="off"
              value={draft.fromAddress}
              onChange={(event) => {
                set('fromAddress', event.target.value);
              }}
              style={textFieldStyle}
            />
            <span className="tx-subtle" style={hintStyle}>
              {STR.settings.email.fromHint}
            </span>
          </label>

          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.settings.email.senderLabel}
            </span>
            <input
              className={error === null ? 'field' : 'field field-over'}
              type="text"
              autoComplete="off"
              value={draft.senderName}
              onChange={(event) => {
                set('senderName', event.target.value);
              }}
              style={textFieldStyle}
            />
          </label>

          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.settings.email.replyToLabel}{' '}
              <span className="tx-subtle">({STR.settings.optional})</span>
            </span>
            <input
              className={error === null ? 'field' : 'field field-over'}
              type="email"
              autoComplete="off"
              placeholder={draft.fromAddress}
              value={draft.replyTo}
              onChange={(event) => {
                set('replyTo', event.target.value);
              }}
              style={textFieldStyle}
            />
            <span className="tx-subtle" style={hintStyle}>
              {STR.settings.email.replyToHint}
            </span>
          </label>

          <div>
            <div style={{ fontSize: 13, fontWeight: 650 }}>{STR.settings.email.eventsTitle}</div>
            <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2 }}>
              {STR.settings.email.eventsHint}
            </span>
            <div
              className="dscroll"
              style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 9 }}
            >
              <table className="dtable">
                <thead>
                  <tr>
                    <th scope="col">{STR.settings.email.columns.event}</th>
                    <th scope="col" style={{ width: 90, textAlign: 'center' }}>
                      {STR.settings.email.columns.email}
                    </th>
                    <th scope="col" style={{ width: 90, textAlign: 'center' }}>
                      {STR.settings.email.columns.inApp}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {draft.events.length === 0 ? (
                    <tr>
                      <td colSpan={3}>
                        <EmptyRow>{STR.settings.email.noEvents}</EmptyRow>
                      </td>
                    </tr>
                  ) : (
                    draft.events.map((event) => (
                      <tr key={event.key} className="row">
                        {/* The server's label: the client never turns a key into text. */}
                        <td style={{ fontSize: 13 }}>{event.label}</td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            aria-label={STR.settings.email.emailFor(event.label)}
                            checked={event.emailEnabled}
                            onChange={(change) => {
                              setEvent(event.key, 'emailEnabled', change.target.checked);
                            }}
                            style={{ ...checkboxStyle, margin: 0 }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            aria-label={STR.settings.email.inAppFor(event.label)}
                            checked={event.inAppEnabled}
                            onChange={(change) => {
                              setEvent(event.key, 'inAppEnabled', change.target.checked);
                            }}
                            style={{ ...checkboxStyle, margin: 0 }}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '12px 22px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            borderRadius: '0 0 11px 11px',
          }}
        >
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            disabled={!dirty}
            onClick={() => {
              setDraft(emailDraftFrom(saved));
              setError(null);
              showToast(STR.toast.emailSettingsDiscarded, 'warn');
            }}
          >
            {STR.settings.discardChanges}
          </button>
          {dirty ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Chip tone="attention">{STR.settings.unsaved}</Chip>
              <button
                type="button"
                className="prim"
                style={primaryButtonStyle}
                disabled={saveMutation.isPending}
                onClick={save}
              >
                {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
              </button>
            </div>
          ) : null}
        </div>
      </Card>

      <TestEmailCard dirty={dirty} />
    </>
  );
}

interface TestOutcome {
  tone: CalloutTone;
  text: string;
}

function outcomeOf(response: EmailTestResponse): TestOutcome {
  const { result } = response;
  if (result.status === 'sent') {
    return { tone: 'positive', text: STR.settings.email.testSent(result.recipient) };
  }
  return {
    tone: 'attention',
    text: STR.settings.email.testSkipped[result.reason](result.recipient),
  };
}

/** The test always goes to the signed-in admin; the request carries no address. */
function TestEmailCard({ dirty }: { dirty: boolean }) {
  const [outcome, setOutcome] = useState<TestOutcome | null>(null);

  const testMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: (response) => {
      setOutcome(outcomeOf(response));
    },
    onError: (failure) => {
      setOutcome({
        tone: 'danger',
        text: failure instanceof ApiError ? failure.message : STR.errors.generic,
      });
    },
  });

  return (
    <Card>
      <CardHeader
        title={STR.settings.email.testTitle}
        action={
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            disabled={testMutation.isPending}
            onClick={() => {
              setOutcome(null);
              testMutation.mutate();
            }}
          >
            {testMutation.isPending ? STR.settings.email.testSending : STR.settings.email.testButton}
          </button>
        }
      />
      <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span className="tx-subtle" style={hintStyle}>
          {dirty ? STR.settings.email.testSaveFirst : STR.settings.email.testHint}
        </span>
        {outcome === null ? null : (
          <Callout tone={outcome.tone} role={outcome.tone === 'danger' ? 'alert' : 'status'}>
            {outcome.text}
          </Callout>
        )}
      </div>
    </Card>
  );
}
